import { Alert, Button, Form, Input } from 'antd';
import { useState } from 'react';
import { errorMessage } from '@/api/client';
import { authApi } from '@/api/endpoints';
import { PASSWORD_RULES } from './validation';

interface Values {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

interface Props {
  currentPasswordLabel?: string;
  submitLabel?: string;
  onChanged: () => void | Promise<void>;
}

export function ChangePasswordForm({
  currentPasswordLabel = 'Mật khẩu hiện tại',
  submitLabel = 'Đổi mật khẩu',
  onChanged,
}: Props) {
  const [form] = Form.useForm<Values>();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (values: Values) => {
    setSubmitting(true);
    setError(null);
    try {
      await authApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      form.resetFields();
      await onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form<Values>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={submitting}
    >
      <Form.Item
        name="currentPassword"
        label={currentPasswordLabel}
        rules={[{ required: true, message: 'Nhập mật khẩu đang dùng' }]}
      >
        <Input.Password autoComplete="current-password" />
      </Form.Item>
      <Form.Item name="newPassword" label="Mật khẩu mới" rules={PASSWORD_RULES}>
        <Input.Password autoComplete="new-password" placeholder="ít nhất 8 ký tự" />
      </Form.Item>
      <Form.Item
        name="newPasswordConfirm"
        label="Gõ lại mật khẩu mới"
        dependencies={['newPassword']}
        rules={[
          { required: true, message: 'Gõ lại mật khẩu mới' },
          ({ getFieldValue }) => ({
            validator: (_, value) =>
              !value || getFieldValue('newPassword') === value
                ? Promise.resolve()
                : Promise.reject(new Error('Hai lần gõ mật khẩu không khớp')),
          }),
        ]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}
      <Button type="primary" htmlType="submit" block loading={submitting}>
        {submitLabel}
      </Button>
    </Form>
  );
}
